import localCache from './localCache';
import { removeThemeData } from './lenderTheme';
import store from '../store';
import { identifyUserActionType } from '../actionTypes';
import { clearLocalstorage, clearSessionStorage } from './index';

const device = {
  Android() {
    return navigator.userAgent.match(/Android/i);
  },
  BlackBerry() {
    return navigator.userAgent.match(/BlackBerry/i);
  },
  iOS() {
    return navigator.userAgent.match(/iPhone|iPod|iPad/i);
  },
  OperaMini() {
    return navigator.userAgent.match(/Opera Mini/i);
  },
  Windows() {
    return navigator.userAgent.match(/IEMobile/i);
  },
  any() {
    return !!(
      device.Android() ||
      device.BlackBerry() ||
      device.iOS() ||
      device.OperaMini() ||
      device.Windows()
    );
  },
};

export const isMobile = device.any();

//Source: https://davidwalsh.name/javascript-debounce-function
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function() {
    const args = arguments;
    const later = function() {
      timeout = null;
      if (!immediate) func.apply(this, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(this, args);
  };
};

export const formatUserCount = (count) => {
  let format = '';
  if (count > 1000 && count < 10000) {
    format = `${count.toString().slice(0, 1)} k+ `;
  } else if (count >= 10000 && count < 100000) {
    format = `${count.toString().slice(0, 2)} k+ `;
  } else if (count >= 100000 && count < 1000000) {
    format = `${count.toString().slice(0, 1)} L+ `;
  } else if (count >= 1000000 && count < 10000000) {
    format = `${count.toString().slice(0, 2)} L+ `;
  } else if (count >= 10000000 && count < 100000000) {
    const first = count.toString().slice(0, 1);
    const second = count.toString().slice(1, 2);
    format = second > 0 ? `${first}.${second} Cr+ ` : `${first} Cr+ `;
  } else if (count >= 100000000 && count < 1000000000) {
    format = `${count.toString().slice(0, 2)} Cr+ `;
  }
  return format;
};

export const calculateEmiDetails = (
  tenure,
  interest,
  amount,
  isCardless = false,
) => {
  if (!interest && !isCardless) {
    return {
      monthlyEmi: null,
      totalAmount: null,
    };
  }
  const interestRate = interest / 100;
  const emiEquationPow = Math.pow(1 + interestRate / 12, tenure);
  const monthlyEmi =
    +amount * (interestRate / 12) * (emiEquationPow / (emiEquationPow - 1));
  const roundedMonthlyEmi = Math.ceil(monthlyEmi);

  return {
    monthlyEmi:
      isCardless && !interest ? Math.ceil(amount / tenure) : roundedMonthlyEmi,
    totalAmount: isCardless && !interest ? amount : tenure * roundedMonthlyEmi,
    purchaseAmount: amount,
    totalInterest:
      isCardless && !interest ? 0 : tenure * roundedMonthlyEmi - amount,
  };
};

export const storageKeys = {
  lenderDetails: 'lender-details-pp',
};

class HandleLenderDetails {
  setCache = (data) => {
    localCache.write(storageKeys.lenderDetails, data, 259200); // 3 days in sec
  };
  getCache = () => {
    return localCache.read(storageKeys.lenderDetails);
  };
  clearCache = () => {
    localCache.clearPrefix(storageKeys.lenderDetails);
  };
}

export const addUtmParams = (
  utmSource,
  utmMedium,
  campaignId,
  utmContent,
  utmTerm,
) => {
  const utmParamsAvailable =
    utmSource || utmMedium || campaignId || utmContent || utmTerm;

  return utmParamsAvailable
    ? `${utmSource ? `&utm_source=${utmSource}` : ''}${
        utmMedium ? `&utm_medium=${utmMedium}` : ''
      }${campaignId ? `&utm_campaign=${campaignId}` : ''}${
        utmContent ? `&utm_content=${utmContent}` : ''
      }${utmTerm ? `&utm_term=${utmTerm}` : ''}`
    : '';
};

export const handleLenderDetails = new HandleLenderDetails();

export const isMerchantenabledForutm = (merchantId) => {
  const whiteListedMerchantIdsForUtm = ['2031825'];
  if (whiteListedMerchantIdsForUtm.includes(merchantId)) {
    return true;
  }
  return false;
};

export const getMerchantInfo = (merchantId) => {
  switch (merchantId) {
    case '470780':
      return {
        logo: 'https://iccdn.in/merchant/logos/zebrs-logo.png',
        offersPage: 'https://instacred.me/offers-zebrs',
        merchantUrl: 'https://zebrs.com/',
      };
    case '809250':
      return {
        logo: 'https://iccdn.in/merchant/logos/mdcomputers-black-logo.png',
        offersPage: 'https://instacred.me/offers-mdcomputers',
        merchantUrl: 'https://mdcomputers.in/',
      };
    case '374847':
      return {
        logo: 'https://iccdn.in/merchant/logos/lot-mobiles-logo.png',
        offersPage: 'https://instacred.me/offers-lotmobiles',
        merchantUrl: 'https://www.lotmobiles.com/',
      };
    case '984235':
      return {
        logo: 'https://iccdn.in/merchant/logos/nilkamal-logo.png',
        offersPage: 'https://instacred.me/offers-nilkamal',
        merchantUrl: 'https://www.nilkamalfurniture.com/',
      };
    case '552163':
      return {
        logo: 'https://iccdn.in/merchant/logos/doctor-dreams-logo.png',
        offersPage: 'https://instacred.me/offers-doctordreams',
        merchantUrl: 'https://doctordreams.com/',
      };
    case '842753':
      return {
        logo: 'https://iccdn.in/merchant/logos/bigc-logo.png',
        offersPage: 'https://instacred.me/offers-bigc',
        merchantUrl: 'https://www.bigcmobiles.com/',
      };
    case '189907':
      return {
        logo: 'https://iccdn.in/merchant/logos/sleepyhead-logo.png',
        offersPage: 'https://instacred.me/offers-sleepyhead',
        merchantUrl: 'https://mysleepyhead.com/',
      };
    case '607561':
      return {
        logo: 'https://iccdn.in/merchant/logos/pepperfry-logo.svg',
        offersPage: 'https://instacred.me/offers-pepperfry',
        merchantUrl: 'https://www.pepperfry.com/',
      };
    case '984235':
      return {
        logo: 'https://iccdn.in/merchant/logos/nilkamal-logo.png',
        offersPage: 'https://instacred.me/offers-nilkamal',
        merchantUrl: 'https://nilkamalfurniture.com/',
      };
    default:
      return {
        logo: false,
        offersPage: false,
        merchantUrl: false,
      };
  }
};

export const getWidgetHeight = (percentageOfScreen) => {
  return (percentageOfScreen / 100) * window.innerHeight;
};

export const getWidgetHeightDesktop = () => {
  return getWidgetHeight(100) > 800 ? 600 : getWidgetHeight(80);
};

export const clearLoginData = () => {
  removeThemeData();
  clearSessionStorage();
  clearLocalstorage();
  store.dispatch({
    type: identifyUserActionType.IDENTIFY_USER_AUTH_RESET,
    payload: {},
  });
};

export const groupBy = (arr, property) => {
  return arr.length > 0
    ? arr.reduce(function(item, index) {
        if (!item[index[property]]) {
          item[index[property]] = [];
        }
        item[index[property]].push(index);
        return item;
      }, {})
    : {};
};

export const isLenderDetailsListForNtb = (lenderDetailsList) => {
  let isLenderForNtb = false;
  for (let i = 0; i < lenderDetailsList.length; i++) {
    if (lenderDetailsList[i].isNtbLender) {
      isLenderForNtb = true;
      break;
    }
  }
  return isLenderForNtb;
};



// WEBPACK FOOTER //
// ./utils/helper.js